/**
 * Parts tab on the project detail page.
 *
 * Three responsibilities:
 *   1. List items (PART → SUB_PART hierarchy) with derived state
 *      (current stage, partial-batch indicator, days-in-stage).
 *   2. Create new parts / sub-parts (only when workflow_state is NOT_STARTED or
 *      ENTERING). Deletion follows the same gate.
 *   3. Surface the parts-approval workflow buttons:
 *        - "Submit for approval" (Assistant or Admin, when ENTERING + items >0)
 *        - "Approve" / "Reject" (Admin only, when PENDING_APPROVAL)
 *        - "Finalize" (Admin only — shortcut bypass for tiny projects)
 *
 * The detailed 18-stage pipeline lives in the Entity Drilldown page; each
 * part name in this list links there. The Parts tab focuses on the parts
 * list + submission flow.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Download, FileText, Paperclip, ChevronRight, Pencil, Plus, Trash2, Truck, Upload, Workflow } from 'lucide-react';
import { formatBpsAsPercent, formatCurrency, formatDate, formatUppercaseBusinessText, stageLabel, uppercaseBusinessText } from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { ExportCsvButton } from '@/components/ui/export-csv-button';
import { Card } from '@/components/ui/card';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FormField, Input } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { SortableHeader } from '@/components/ui/sortable-header';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { WarningConfirmDialog } from '@/components/warning-confirm-dialog';
import { AttachmentBadge } from '@/components/attachments/attachment-badge';
import { AttachmentDetailDrawer } from '@/components/attachments/detail-drawer';
import { detectAdminWarning } from '@/lib/admin-warnings';
import {
  uploadFileToMinio,
  useAttachmentCounts,
  useAttachmentsList,
  useCreateDocument,
  type Attachment,
  type UploadProgress,
} from '@/lib/attachments-queries';
import { useCurrentUser } from '@/lib/auth';
import { useDiscardDraft, useDraftsList, useSaveDraft } from '@/lib/draft-queries';
import { isLockedForUser } from '@/lib/closure-lock';
import { isValidNonNegativePriceInput } from '@/lib/form-validators';
import { suggestNextSrNumber } from '@/lib/sr-number';
import {
  useApproveSubmission,
  useCreateProjectItem,
  useCreateDeliveryEvent,
  useDeleteProjectItem,
  useDeliveryEvents,
  useFinalizeParts,
  useImportScheduleCsv,
  usePartsSubmissions,
  useProjectItems,
  useRejectSubmission,
  useSubmitParts,
  useUpdateProjectItem,
  type DeliveryEvent,
  type ProjectItem,
  type ScheduleCsvImportResult,
} from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';

interface Props {
  project: Project;
}

function uppercaseInput(value: string): string {
  return value.toLocaleUpperCase('en-US');
}

function requiredBusiness(value: string): string {
  return uppercaseBusinessText(value);
}

function optionalBusiness(value: string): string | null {
  const upper = uppercaseBusinessText(value);
  return upper === '' ? null : upper;
}

function draftString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const MAX_SOS_PDF_BYTES = 250 * 1024 * 1024;

export function ProjectPartsTab({ project }: Props) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const items = useProjectItems(project.id);
  const submissions = usePartsSubmissions(project.id);
  // E26 — per-part attachment counts. Reads from the same cached payload
  // the project Documents tab already pulls, so no extra request on tab
  // switching.
  const counts = useAttachmentCounts(project.id);

  const [showCreate, setShowCreate] = useState<{ parentId: string | null } | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showRejectFor, setShowRejectFor] = useState<string | null>(null);
  // Patch 5 — plain pre-confirmation state (replaces window.confirm calls).
  // Distinct from `pendingWarning` below, which is for SERVER-side warnings
  // surfaced on retry; this one fires BEFORE the mutation runs.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);
  // Patch 5 — sortable. Client-side sort (items are fetched in one batch
  // per project so no need to re-query on toggle). Default srNumber asc
  // matches the server's natural ordering.
  type PartsSortKey = 'srNumber' | 'itemNumber' | 'name' | 'currentStageNumber';
  const [sortBy, setSortBy] = useState<PartsSortKey>('srNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (key: PartsSortKey): void => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      // For srNumber the natural default is asc (smallest first); for the
      // others desc tends to be more useful (latest stage, Z first when
      // searching by name probably isn't what users want, so still asc).
      setSortDir(key === 'currentStageNumber' ? 'desc' : 'asc');
    }
  };

  const submit = useSubmitParts(project.id);
  const approve = useApproveSubmission(project.id);
  const reject = useRejectSubmission(project.id);
  const finalize = useFinalizeParts(project.id);

  if (items.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const allItems = items.data ?? [];
  // Patch 5 — sortable. Default: srNumber asc (matches server's natural
  // ordering). Sub-parts always inherit the parent's position; we sort
  // PARTS at the top level.
  const partsAllUnsorted = allItems.filter((i) => i.itemType === 'PART');
  const parts = [...partsAllUnsorted].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'itemNumber':
        return String(a.itemNumber ?? '').localeCompare(String(b.itemNumber ?? '')) * dir;
      case 'name':
        return a.name.localeCompare(b.name) * dir;
      case 'currentStageNumber':
        return (a.currentStageNumber - b.currentStageNumber) * dir;
      case 'srNumber':
      default: {
        const aN = a.srNumber ?? Number.POSITIVE_INFINITY;
        const bN = b.srNumber ?? Number.POSITIVE_INFINITY;
        return (aN - bN) * dir;
      }
    }
  });
  const subPartsByParent = new Map<string, ProjectItem[]>();
  for (const sp of allItems.filter((i) => i.itemType === 'SUB_PART')) {
    if (!sp.parentItemId) continue;
    const list = subPartsByParent.get(sp.parentItemId) ?? [];
    list.push(sp);
    subPartsByParent.set(sp.parentItemId, list);
  }

  const canEdit =
    project.partsWorkflowState === 'NOT_STARTED' || project.partsWorkflowState === 'ENTERING';
  // Spec §13 — "Submit Parts for Approval | ✓ | ✓"; "Edit closed project | ✓ | ✗".
  // Admin retains edit access on closed projects (§9), so the closure check is role-aware.
  const locked = isLockedForUser(project, isAdmin);
  const canSubmit =
    canEdit &&
    parts.length > 0 &&
    !locked &&
    (project.lifecycleState === 'CONTRACT_SIGNED' || project.partsWorkflowState === 'ENTERING');
  const pendingSubmission = submissions.data?.find((s) => s.status === 'PENDING') ?? null;

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <StatusBadge kind="partsWorkflow" value={project.partsWorkflowState} />
          {project.partsWorkflowState === 'FINALISED' ? (
            <span className="text-xs text-secondary">
              Locked — parts list is frozen. Pipeline progress continues on batches and billing.
            </span>
          ) : project.partsWorkflowState === 'PENDING_APPROVAL' ? (
            <span className="text-xs text-secondary">
              Awaiting admin review. Parts cannot be edited until approval or rejection.
            </span>
          ) : project.partsWorkflowState === 'NOT_STARTED' ? (
            <span className="text-xs text-secondary">
              Add the first part to start entering the parts list.
            </span>
          ) : (
            <span className="text-xs text-secondary">
              Add parts and sub-parts, then submit for approval when ready.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !locked ? (
            <Button
              variant="tertiary"
              size="compact"
              leftIcon={<Upload className="h-3.5 w-3.5" />}
              onClick={() => setShowImport(true)}
            >
              Import CSV
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="secondary"
              size="compact"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setShowCreate({ parentId: null })}
            >
              Add part
            </Button>
          ) : null}
          {canSubmit && !pendingSubmission ? (
            <Button
              variant="primary"
              size="compact"
              loading={submit.isPending}
              onClick={() => submit.mutate()}
            >
              Submit for approval
            </Button>
          ) : null}
          {pendingSubmission && isAdmin ? (
            <>
              <Button
                variant="destructive"
                size="compact"
                onClick={() => setShowRejectFor(pendingSubmission.id)}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                size="compact"
                loading={approve.isPending}
                onClick={() => approve.mutate({ submissionId: pendingSubmission.id })}
              >
                Approve
              </Button>
            </>
          ) : null}
          {canEdit && isAdmin && parts.length > 0 && !pendingSubmission ? (
            <Button
              variant="tertiary"
              size="compact"
              loading={finalize.isPending}
              onClick={() => {
                setPendingConfirm({
                  title: 'Finalize parts list?',
                  message:
                    'Finalize the parts list now, skipping approval. This action is logged and cannot be undone.',
                  confirmLabel: 'Finalize now',
                  onConfirm: () => finalize.mutate(),
                });
              }}
            >
              Finalize directly
            </Button>
          ) : null}
          {parts.length > 0 ? (
            <ExportCsvButton
              href={`/api/exports/projects/${project.id}/parts`}
              fallbackFilename="parts.csv"
            />
          ) : null}
        </div>
      </div>

      {submit.error ? (
        <Alert tone="danger" className="mb-3">
          {submit.error.message}
        </Alert>
      ) : null}
      {approve.error ? (
        <Alert tone="danger" className="mb-3">
          {approve.error.message}
        </Alert>
      ) : null}
      {reject.error ? (
        <Alert tone="danger" className="mb-3">
          {reject.error.message}
        </Alert>
      ) : null}
      {finalize.error ? (
        <Alert tone="danger" className="mb-3">
          {finalize.error.message}
        </Alert>
      ) : null}

      <ScheduleOfStoresPdfPanel projectId={project.id} canEdit={canEdit && !locked} />

      <Card bodyClassName="p-0">
        {parts.length === 0 ? (
          <EmptyState
            icon={<Workflow className="h-6 w-6" />}
            title="No parts yet"
            body="Add the first part to start the procurement pipeline. Parts come in three variants: stand-alone (B), composed of sub-parts (A), or a sub-part of another (C)."
            action={
              canEdit ? (
                <Button
                  variant="primary"
                  size="compact"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => setShowCreate({ parentId: null })}
                >
                  Add the first part
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Patch 5 — sort bar. Parts render as cards (not a flat
                table), so we expose sort as a button row matching the
                /communications page. */}
            <div className="flex items-center gap-3 border-b border-border-default px-4 py-2 text-xs text-secondary">
              <span className="font-semibold uppercase tracking-wide">Sort</span>
              <SortableHeader
                label="Sr #"
                active={sortBy === 'srNumber'}
                direction={sortDir}
                onToggle={() => toggleSort('srNumber')}
              />
              <SortableHeader
                label="Part #"
                active={sortBy === 'itemNumber'}
                direction={sortDir}
                onToggle={() => toggleSort('itemNumber')}
              />
              <SortableHeader
                label="Noun"
                active={sortBy === 'name'}
                direction={sortDir}
                onToggle={() => toggleSort('name')}
              />
              <SortableHeader
                label="Stage"
                active={sortBy === 'currentStageNumber'}
                direction={sortDir}
                onToggle={() => toggleSort('currentStageNumber')}
              />
            </div>
            <div className="divide-y divide-border-default">
              {parts.map((part) => (
                <PartRow
                  key={part.id}
                  project={project}
                  part={part}
                  subParts={subPartsByParent.get(part.id) ?? []}
                  canEdit={canEdit}
                  onAddSubPart={() => setShowCreate({ parentId: part.id })}
                  attachmentCount={counts.data?.byPart[part.id] ?? 0}
                />
              ))}
            </div>
          </>
        )}
      </Card>

      {showCreate ? (
        <CreateItemModal
          project={project}
          parentId={showCreate.parentId}
          existingSrNumbers={
            showCreate.parentId === null
              ? parts.map((p) => p.srNumber)
              : (subPartsByParent.get(showCreate.parentId) ?? []).map((p) => p.srNumber)
          }
          onClose={() => setShowCreate(null)}
        />
      ) : null}

      {showImport ? (
        <ImportScheduleCsvModal project={project} onClose={() => setShowImport(false)} />
      ) : null}

      {showRejectFor ? (
        <RejectModal
          projectId={project.id}
          submissionId={showRejectFor}
          onClose={() => setShowRejectFor(null)}
        />
      ) : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          pending={finalize.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}


// ---------------------------------------------------------------------------
// Schedule of Stores official PDF attachment
// ---------------------------------------------------------------------------

function ScheduleOfStoresPdfPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const docs = useAttachmentsList({
    projectId,
    kind: 'DOCUMENT',
    documentType: 'SCHEDULE_OF_STORES',
    projectLevelOnly: true,
    pageSize: 10,
  });
  const createDocument = useCreateDocument();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const attachments = docs.data?.items ?? [];
  const latest = attachments[0] ?? null;

  const onFile = (file: File | undefined): void => {
    setError(null);
    setProgress(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    const looksLikePdf =
      file.type === 'application/pdf' || file.name.toLocaleLowerCase('en-US').endsWith('.pdf');
    if (!looksLikePdf) {
      setSelectedFile(null);
      setError('Please select the official Schedule of Stores PDF file.');
      return;
    }
    if (file.size > MAX_SOS_PDF_BYTES) {
      setSelectedFile(null);
      setError(`File is too large (${humanSize(file.size)}). Maximum is 250 MB.`);
      return;
    }
    setSelectedFile(file);
  };

  const upload = async (): Promise<void> => {
    if (!selectedFile || uploading || createDocument.isPending) return;
    setError(null);
    setUploading(true);
    try {
      const { storageKey } = await uploadFileToMinio(selectedFile, setProgress);
      await createDocument.mutateAsync({
        storageKey,
        fileSizeBytes: selectedFile.size,
        fileMimeType: selectedFile.type || 'application/pdf',
        displayName: requiredBusiness(selectedFile.name.replace(/\.pdf$/i, '') || 'SCHEDULE OF STORES'),
        documentType: 'SCHEDULE_OF_STORES',
        documentDate: todayIso(),
        description: 'OFFICIAL SCHEDULE OF STORES PDF',
        anchor: { projectId },
        tags: ['SCHEDULE-OF-STORES', 'SOS'],
        supersedesId: latest?.id ?? null,
      });
      setSelectedFile(null);
      setProgress(null);
      await docs.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload Schedule of Stores PDF.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="mb-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-tertiary" strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-primary">Schedule of Stores PDF</h3>
            {attachments.length > 0 ? <Badge tone="neutral">{attachments.length}</Badge> : null}
          </div>
          <p className="mt-1 text-xs text-secondary">
            Attach the official SOS PDF here. The newest upload supersedes the previous SOS PDF.
          </p>
          {latest ? (
            <button
              type="button"
              onClick={() => setSelectedAttachmentId(latest.id)}
              className="mt-2 inline-flex max-w-full items-center gap-1 truncate text-xs text-accent-strong hover:underline"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
              <span className="truncate">Latest: {formatUppercaseBusinessText(latest.displayName)}</span>
            </button>
          ) : (
            <div className="mt-2 text-xs text-secondary">No SOS PDF attached yet.</div>
          )}
        </div>

        {canEdit ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border-default bg-base px-3 py-2 text-xs font-medium text-secondary hover:border-border-strong hover:text-primary">
              <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span>{selectedFile ? selectedFile.name : 'Choose PDF'}</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                onChange={(event) => onFile(event.currentTarget.files?.[0])}
              />
            </label>
            <Button
              variant="primary"
              size="compact"
              onClick={() => void upload()}
              disabled={!selectedFile || uploading || createDocument.isPending}
            >
              {uploading || createDocument.isPending
                ? progress
                  ? `Uploading ${progress.percent}%`
                  : 'Uploading…'
                : latest
                  ? 'Replace SOS PDF'
                  : 'Upload SOS PDF'}
            </Button>
          </div>
        ) : null}
      </div>

      {docs.isError ? (
        <Alert tone="danger" className="mt-3">
          Could not load Schedule of Stores PDF attachments.
        </Alert>
      ) : null}
      {error || createDocument.error ? (
        <Alert tone="danger" className="mt-3">
          {error ?? createDocument.error?.message}
        </Alert>
      ) : null}
      {attachments.length > 1 ? (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {attachments.slice(0, 5).map((doc: Attachment) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => setSelectedAttachmentId(doc.id)}
              className="inline-flex items-center gap-1 rounded-sm border border-border-default px-2 py-1 text-secondary hover:border-border-strong hover:text-primary"
            >
              <Download className="h-3 w-3" strokeWidth={1.5} />
              {formatUppercaseBusinessText(doc.displayName)}
            </button>
          ))}
        </div>
      ) : null}

      <AttachmentDetailDrawer
        attachmentId={selectedAttachmentId}
        onClose={() => setSelectedAttachmentId(null)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Part row — header row + nested sub-parts table
// ---------------------------------------------------------------------------

function PartRow({
  project,
  part,
  subParts,
  canEdit,
  onAddSubPart,
  attachmentCount,
}: {
  project: Project;
  part: ProjectItem;
  subParts: ProjectItem[];
  canEdit: boolean;
  onAddSubPart: () => void;
  attachmentCount: number;
}) {
  const variant = part.hasSubParts ? 'A' : 'B';
  // E19: surface both the padded stage number AND its name. Bare "Stage 7"
  // requires the reader to remember the 18-stage map; "Stage 07 — Arrived"
  // doesn't. Zero-padding keeps the labels visually aligned across cards
  // when a mix of stage numbers is on screen.
  const currentStageDisplay =
    part.currentStageNumber > 0
      ? `Stage ${String(part.currentStageNumber).padStart(2, '0')} — ${stageLabel(part.currentStageNumber)}`
      : '—';
  const del = useDeleteProjectItem(project.id);
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const locked = isLockedForUser(project, isAdmin);
  const [editingItem, setEditingItem] = useState<ProjectItem | null>(null);
  const [showDelivery, setShowDelivery] = useState(false);
  const deliveredQuantity = part.deliveredQuantity ?? 0;
  const requiredDeliveryQuantity = part.partQuantity ?? 0;
  const remainingDeliveryQuantity = part.remainingDeliveryQuantity ?? Math.max(0, requiredDeliveryQuantity - deliveredQuantity);
  const canRecordDelivery =
    project.partsWorkflowState === 'FINALISED' &&
    !locked &&
    requiredDeliveryQuantity > 0 &&
    remainingDeliveryQuantity > 0;

  // Patch 5 — local pre-confirm state for delete-part + delete-sub-part.
  // Separate from `pendingWarning` (server warnings on retry); this one
  // fires BEFORE the mutation. Replaces native window.confirm dialogs.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);

  // Pass 23 / Pass 26 — delete warning retry flow.
  // The server may reject a DELETE with up to three different WARNING_*
  // codes (billing, active-pipeline, sub-part-with-batches). The flow:
  //   1. attemptDelete fires the mutation with whatever acks we have
  //   2. If it fails with a WARNING_* code, we stage the message + code
  //   3. User confirms → confirmDelete adds the code's ack flag and retries
  //   4. If the retry surfaces a DIFFERENT warning, the cycle repeats —
  //      a deletion that triggers all three may need three confirmations.
  //   5. Non-warning errors propagate normally via del.error.
  interface PendingAcks {
    acknowledgeDeleteWithBilling?: boolean;
    acknowledgeDeleteWithActivePipeline?: boolean;
    acknowledgeDeleteSubPartWithBatches?: boolean;
  }
  const [pendingWarning, setPendingWarning] = useState<{
    itemId: string;
    rowVersion: number;
    itemLabel: string;
    message: string;
    code: 'DELETE_WITH_BILLING' | 'DELETE_WITH_ACTIVE_PIPELINE' | 'DELETE_SUBPART_WITH_BATCHES';
    /** Acks already collected from prior rounds of this same delete. */
    acks: PendingAcks;
  } | null>(null);

  /** Map a warning code to the ack flag the server expects on retry. */
  function ackFlagFor(
    code: 'DELETE_WITH_BILLING' | 'DELETE_WITH_ACTIVE_PIPELINE' | 'DELETE_SUBPART_WITH_BATCHES',
  ): keyof PendingAcks {
    switch (code) {
      case 'DELETE_WITH_BILLING':
        return 'acknowledgeDeleteWithBilling';
      case 'DELETE_WITH_ACTIVE_PIPELINE':
        return 'acknowledgeDeleteWithActivePipeline';
      case 'DELETE_SUBPART_WITH_BATCHES':
        return 'acknowledgeDeleteSubPartWithBatches';
    }
  }

  async function attemptDelete(
    itemId: string,
    rowVersion: number,
    itemLabel: string,
    acks: PendingAcks = {},
  ) {
    try {
      await del.mutateAsync({ itemId, rowVersion, ...acks });
      setPendingWarning(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const warning = detectAdminWarning(message);
      if (
        warning &&
        (warning.code === 'DELETE_WITH_BILLING' ||
          warning.code === 'DELETE_WITH_ACTIVE_PIPELINE' ||
          warning.code === 'DELETE_SUBPART_WITH_BATCHES')
      ) {
        setPendingWarning({
          itemId,
          rowVersion,
          itemLabel,
          message: warning.message,
          code: warning.code,
          acks,
        });
        return;
      }
      // Unknown error — surfaced via del.error inline.
    }
  }

  async function confirmDelete() {
    if (!pendingWarning) return;
    const flag = ackFlagFor(pendingWarning.code);
    const nextAcks: PendingAcks = { ...pendingWarning.acks, [flag]: true };
    await attemptDelete(
      pendingWarning.itemId,
      pendingWarning.rowVersion,
      pendingWarning.itemLabel,
      nextAcks,
    );
  }

  const subPartCols: ColumnDef<ProjectItem>[] = [
    { id: 'sr', header: 'Sr#', width: '70px', cell: (r) => r.srNumber ?? '—', numeric: true },
    {
      id: 'itemNumber',
      header: 'Sub-part #',
      width: '120px',
      cell: (r) => formatUppercaseBusinessText(r.itemNumber),
    },
    {
      id: 'name',
      header: 'Noun',
      cell: (r) => (
        <Link
          to="/projects/$projectId/items/$itemId"
          params={{ projectId: project.id, itemId: r.id }}
          className="text-primary hover:text-accent-strong hover:underline"
        >
          {formatUppercaseBusinessText(r.name)}
        </Link>
      ),
    },
    {
      id: 'makeBrand',
      header: 'Make/brand',
      width: '130px',
      cell: (r) => formatUppercaseBusinessText(r.makeBrand) || '—',
    },
    {
      id: 'origin',
      header: 'Origin',
      width: '110px',
      cell: (r) => formatUppercaseBusinessText(r.origin) || '—',
    },
    {
      id: 'remarks',
      header: 'Remarks',
      width: '150px',
      cell: (r) => formatUppercaseBusinessText(r.remarks) || '—',
    },
    {
      id: 'qty',
      header: 'Quantity',
      numeric: true,
      width: '100px',
      cell: (r) => r.quantityPerParent ?? '—',
    },
    {
      id: 'supplierPrice',
      header: 'Supplier price',
      numeric: true,
      width: '130px',
      cell: (r) =>
        r.supplierUnitPricePaisa != null
          ? formatCurrency(r.supplierUnitPricePaisa, { prefix: false })
          : '—',
    },
    {
      id: 'ordered',
      header: 'Ordered',
      numeric: true,
      width: '90px',
      cell: (r) => r.activeOrderedQuantity,
    },
    {
      id: 'partial',
      header: '',
      width: '90px',
      cell: (r) =>
        r.partialBatchIndicator ? <Badge tone="warning">{r.partialBatchIndicator}</Badge> : null,
    },
    {
      id: 'stage',
      header: 'Stage',
      width: '80px',
      cell: (r) => (
        <span
          className="text-xs text-secondary"
          title={r.currentStageNumber > 0 ? stageLabel(r.currentStageNumber) : undefined}
        >
          {r.currentStageNumber > 0 ? `S${String(r.currentStageNumber).padStart(2, '0')}` : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '72px',
      cell: (r) =>
        canEdit ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              aria-label="Edit sub-part"
              onClick={() => setEditingItem(r)}
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="Delete sub-part"
              onClick={() => {
                setPendingConfirm({
                  title: 'Delete sub-part?',
                  message: `Delete sub-part "${formatUppercaseBusinessText(r.name)}"? This action is logged.`,
                  confirmLabel: 'Delete',
                  onConfirm: () => void attemptDelete(r.id, r.rowVersion, r.name),
                });
              }}
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-status-danger"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-secondary tabular-nums">Sr {part.srNumber}</span>
            <span className="font-mono text-xs text-secondary tabular-nums">
              {formatUppercaseBusinessText(part.itemNumber)}
            </span>
            <Link
              to="/projects/$projectId/items/$itemId"
              params={{ projectId: project.id, itemId: part.id }}
              className="font-medium text-primary hover:text-accent-strong hover:underline"
            >
              {formatUppercaseBusinessText(part.name)}
            </Link>
            <Badge tone="neutral">Variant {variant}</Badge>
            {part.partialBatchIndicator ? (
              <Badge tone="warning">{part.partialBatchIndicator}</Badge>
            ) : null}
            {/* E26 — attachment indicator */}
            <AttachmentBadge count={attachmentCount} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-secondary">
            <span>UOM: {formatUppercaseBusinessText(part.uom)}</span>
            <span>Make/brand: {formatUppercaseBusinessText(part.makeBrand) || '—'}</span>
            <span>Origin: {formatUppercaseBusinessText(part.origin) || '—'}</span>
            <span>Remarks: {formatUppercaseBusinessText(part.remarks) || '—'}</span>
            <span>Qty: {part.partQuantity ?? '—'}</span>
            <span>Unit price: {part.clientUnitPricePaisa != null ? formatCurrency(part.clientUnitPricePaisa) : '—'}</span>
            <span>Value: {part.valuePaisa != null ? formatCurrency(part.valuePaisa) : '—'}</span>
            <span>GST: {formatBpsAsPercent(part.gstRateBps)}</span>
            <span>GST amount: {part.gstAmountPaisa != null ? formatCurrency(part.gstAmountPaisa) : '—'}</span>
            <span>Total value: {part.totalValuePaisa != null ? formatCurrency(part.totalValuePaisa) : '—'}</span>
            <span>Ordered: {part.activeOrderedQuantity}</span>
            <span>Delivered: {deliveredQuantity}</span>
            <span>Remaining delivery: {remainingDeliveryQuantity}</span>
            <span>Current: {currentStageDisplay}</span>
            {part.daysInCurrentStage > 0 ? (
              <span className="text-secondary">{part.daysInCurrentStage} days in stage</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canRecordDelivery ? (
            <Button
              variant="secondary"
              size="compact"
              leftIcon={<Truck className="h-3.5 w-3.5" />}
              onClick={() => setShowDelivery(true)}
            >
              Record delivery
            </Button>
          ) : null}
          {canEdit && variant !== 'B' && subParts.length === 0 ? null : null}
          {canEdit ? (
            <>
              <Button
                variant="tertiary"
                size="compact"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={onAddSubPart}
              >
                Add sub-part
              </Button>
              <button
                type="button"
                aria-label="Edit part"
                onClick={() => setEditingItem(part)}
                className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
              >
                <Pencil className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                aria-label="Delete part"
                onClick={() => {
                  setPendingConfirm({
                    title: 'Delete part?',
                    message: `Delete part "${formatUppercaseBusinessText(part.name)}"? This action is logged.`,
                    confirmLabel: 'Delete',
                    onConfirm: () => void attemptDelete(part.id, part.rowVersion, part.name),
                  });
                }}
                className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-status-danger"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {subParts.length > 0 ? (
        <div className="mt-3 rounded-md border border-border-default bg-base/40">
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-tertiary">
            <ChevronRight className="mr-1 inline h-3 w-3" />
            Sub-parts ({subParts.length})
          </div>
          <DataTable<ProjectItem> columns={subPartCols} rows={subParts} rowKey={(r) => r.id} />
        </div>
      ) : null}

      {pendingWarning ? (
        <WarningConfirmDialog
          open
          title={`Delete "${pendingWarning.itemLabel}"?`}
          message={pendingWarning.message}
          confirmLabel="Delete anyway"
          pending={del.isPending}
          onCancel={() => setPendingWarning(null)}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          pending={del.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}

      {showDelivery ? (
        <DeliveryEventModal project={project} part={part} onClose={() => setShowDelivery(false)} />
      ) : null}

      {editingItem ? (
        <EditItemModal
          project={project}
          item={editingItem}
          onClose={() => setEditingItem(null)}
        />
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Delivery event modal — partial/final delivery quantities
// ---------------------------------------------------------------------------

function DeliveryEventModal({
  project,
  part,
  onClose,
}: {
  project: Project;
  part: ProjectItem;
  onClose: () => void;
}) {
  const events = useDeliveryEvents(project.id, part.id);
  const create = useCreateDeliveryEvent(project.id, part.id);
  const requiredQuantity = part.partQuantity ?? 0;
  const deliveredQuantity = part.deliveredQuantity ?? 0;
  const remainingQuantity = part.remainingDeliveryQuantity ?? Math.max(0, requiredQuantity - deliveredQuantity);
  const [quantity, setQuantity] = useState(() => String(Math.max(1, remainingQuantity || requiredQuantity || 1)));
  const [deliveryDate, setDeliveryDate] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [remarks, setRemarks] = useState('');

  const quantityNumber = Number(quantity);
  const validQuantity = Number.isInteger(quantityNumber) && quantityNumber > 0 && quantityNumber <= remainingQuantity;
  const canSubmit = validQuantity && deliveryDate.trim().length > 0 && remainingQuantity > 0;

  const submit = (): void => {
    if (!canSubmit) return;
    create.mutate(
      {
        quantity: quantityNumber,
        deliveryDate,
        referenceNumber: optionalBusiness(referenceNumber),
        remarks: optionalBusiness(remarks),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Record delivery"
      description={`${formatUppercaseBusinessText(part.name)} · remaining quantity ${remainingQuantity}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit} loading={create.isPending}>
            Save delivery
          </Button>
        </>
      }
    >
      {create.error ? (
        <Alert tone="danger" className="mb-3">
          {create.error.message}
        </Alert>
      ) : null}
      {events.error ? (
        <Alert tone="danger" className="mb-3">
          {events.error.message}
        </Alert>
      ) : null}
      {!project.partialDeliveryAllowed ? (
        <Alert tone="warning" className="mb-3">
          Partial delivery is not allowed for this project. The backend will accept only the full remaining quantity.
        </Alert>
      ) : null}

      <div className="mb-3 grid gap-2 rounded-sm border border-border-default bg-subtle/40 px-3 py-2 text-xs text-secondary sm:grid-cols-3">
        <span>Required: {requiredQuantity}</span>
        <span>Delivered: {deliveredQuantity}</span>
        <span>Remaining: {remainingQuantity}</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Quantity delivered" required>
          <Input
            autoFocus
            type="number"
            min={1}
            max={remainingQuantity || undefined}
            step={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="font-mono tabular-nums"
          />
        </FormField>
        <FormField label="Delivery date" required>
          <DmyDateInput value={deliveryDate} onChange={setDeliveryDate} />
        </FormField>
      </div>
      <FormField label="Reference number">
        <Input
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(uppercaseInput(e.target.value))}
          placeholder="e.g. DC-001"
        />
      </FormField>
      <FormField label="Remarks">
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(uppercaseInput(e.target.value))}
          rows={2}
          className="w-full rounded-md border border-border-default bg-base px-3 py-2 text-sm text-primary outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          placeholder="Optional delivery remarks"
        />
      </FormField>

      <DeliveryHistory events={events.data ?? []} loading={events.isLoading} />
    </Modal>
  );
}

function DeliveryHistory({ events, loading }: { events: DeliveryEvent[]; loading: boolean }) {
  if (loading) {
    return <div className="mt-3 text-xs text-secondary">Loading delivery history…</div>;
  }
  if (events.length === 0) {
    return <div className="mt-3 text-xs text-secondary">No delivery records yet.</div>;
  }
  return (
    <div className="mt-4 overflow-hidden rounded-md border border-border-default">
      <table className="min-w-full divide-y divide-border-default text-xs">
        <thead className="bg-subtle text-left text-tertiary">
          <tr>
            <th className="px-2 py-1">Date</th>
            <th className="px-2 py-1 text-right">Quantity</th>
            <th className="px-2 py-1">Reference</th>
            <th className="px-2 py-1">Remarks</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-default">
          {events.map((event) => (
            <tr key={event.id}>
              <td className="px-2 py-1 tabular-nums">{formatDate(event.deliveryDate)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{event.quantity}</td>
              <td className="px-2 py-1">{formatUppercaseBusinessText(event.referenceNumber) || '—'}</td>
              <td className="px-2 py-1">{formatUppercaseBusinessText(event.remarks) || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Schedule of Stores CSV import modal
// ---------------------------------------------------------------------------

function ImportScheduleCsvModal({ project, onClose }: { project: Project; onClose: () => void }) {
  const importer = useImportScheduleCsv(project.id);
  const csvDrafts = useDraftsList({ draftType: 'CSV_IMPORT', projectId: project.id });
  const saveDraft = useSaveDraft();
  const discardDraft = useDiscardDraft();
  const [csvText, setCsvText] = useState('');
  const [filename, setFilename] = useState('');
  const [preview, setPreview] = useState<ScheduleCsvImportResult | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  const latestCsvDraft = csvDrafts.data?.items[0] ?? null;

  const canPreview = csvText.trim().length > 0;
  const canImport = Boolean(preview && preview.errors.length === 0 && preview.rowsValid > 0);

  const saveCsvDraft = async (): Promise<void> => {
    await saveDraft.mutateAsync({
      draftType: 'CSV_IMPORT',
      projectId: project.id,
      payloadJson: { csvText, filename },
    });
  };

  const resumeCsvDraft = (): void => {
    if (!latestCsvDraft) return;
    const payload = latestCsvDraft.payloadJson;
    setCsvText(typeof payload.csvText === 'string' ? payload.csvText : '');
    setFilename(typeof payload.filename === 'string' ? payload.filename : '');
    setPreview(null);
  };

  const discardCsvDraft = (): void => {
    if (!latestCsvDraft) return;
    discardDraft.mutate({ id: latestCsvDraft.id, rowVersion: latestCsvDraft.rowVersion });
  };

  const readFile = (file: File): void => {
    setReadError(null);
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(typeof reader.result === 'string' ? reader.result : '');
      setPreview(null);
    };
    reader.onerror = () => setReadError('Could not read the selected CSV file.');
    reader.readAsText(file);
  };

  const runPreview = async (): Promise<void> => {
    const result = await importer.mutateAsync({ csvText, dryRun: true });
    setPreview(result);
  };

  const runImport = async (): Promise<void> => {
    const result = await importer.mutateAsync({ csvText, dryRun: false });
    setPreview(result);
    if (result.rowsImported > 0) {
      if (latestCsvDraft) {
        discardDraft.mutate({ id: latestCsvDraft.id, rowVersion: latestCsvDraft.rowVersion });
      }
      onClose();
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Import Schedule of Stores CSV"
      description="Upload a CSV with PART and optional SUB-PART rows. Preview first, then import valid rows. Business text is saved in uppercase."
      size="lg"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => void saveCsvDraft()}
            disabled={!canPreview}
            loading={saveDraft.isPending}
          >
            Save draft
          </Button>
          <Button
            variant="secondary"
            onClick={() => void runPreview()}
            disabled={!canPreview}
            loading={importer.isPending && (!preview || preview.dryRun)}
          >
            Preview
          </Button>
          <Button
            variant="primary"
            onClick={() => void runImport()}
            disabled={!canImport}
            loading={importer.isPending && preview?.dryRun === false}
          >
            Import rows
          </Button>
        </>
      }
    >
      {importer.error ? (
        <Alert tone="danger" className="mb-3">
          {importer.error.message}
        </Alert>
      ) : null}
      {readError ? (
        <Alert tone="danger" className="mb-3">
          {readError}
        </Alert>
      ) : null}
      {saveDraft.error ? (
        <Alert tone="danger" className="mb-3">
          {saveDraft.error.message}
        </Alert>
      ) : null}
      {discardDraft.error ? (
        <Alert tone="danger" className="mb-3">
          {discardDraft.error.message}
        </Alert>
      ) : null}
      {latestCsvDraft ? (
        <Alert tone="info" className="mb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>A saved CSV import draft is available for this project.</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="compact" type="button" onClick={resumeCsvDraft}>
                Resume draft
              </Button>
              <Button
                variant="tertiary"
                size="compact"
                type="button"
                loading={discardDraft.isPending}
                onClick={discardCsvDraft}
              >
                Discard
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}

      <div className="mb-3 rounded-md border border-border-default bg-subtle/40 p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-primary">Start from the official CSV template</div>
            <p className="mt-1 text-xs text-secondary">
              Download the template to get the exact importer headers, UOM-before-quantity order, and sample PART/SUB-PART rows.
            </p>
          </div>
          <ExportCsvButton
            href={`/api/projects/${project.id}/items/import-csv-template`}
            fallbackFilename="schedule-of-stores-template.csv"
            label="Download CSV template"
            variant="secondary"
          />
        </div>
      </div>

      <FormField
        label="CSV file"
        helpText="Supported headers include TYPE, SR #, PART / SUB-PART #, NOUN, UOM, QUANTITY, PARENT, UNIT PRICE, GST %, SUPPLIER PRICE, MAKE/BRAND, ORIGIN, REMARKS."
      >
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file) readFile(file);
          }}
          className="block w-full text-sm text-secondary file:mr-3 file:rounded-md file:border-0 file:bg-subtle file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-border-default"
        />
      </FormField>

      <FormField label="CSV text" helpText={filename ? `Loaded: ${filename}` : 'You can also paste CSV text directly.'}>
        <textarea
          value={csvText}
          onChange={(event) => {
            setCsvText(event.target.value);
            setPreview(null);
          }}
          rows={8}
          className="w-full rounded-md border border-border-default bg-base px-3 py-2 font-mono text-xs text-primary outline-none focus:border-accent"
          placeholder={'TYPE,SR #,PART / SUB-PART #,NOUN,UOM,QUANTITY,PARENT,UNIT PRICE,GST %,SUPPLIER PRICE,MAKE/BRAND,ORIGIN,REMARKS\nPART,1,PN-001,RADAR MODULE,NOS,10,,12500,18,10000,HONEYWELL,USA,MAIN ASSEMBLY\nSUB-PART,1,SP-001,ROTARY JOINT,,2,PN-001,,,1500,HONEYWELL,USA,MATCHED SET'}
        />
      </FormField>

      {preview ? (
        <div className="mt-4 space-y-3">
          <div className="grid gap-2 text-xs sm:grid-cols-4">
            <div className="rounded-md border border-border-default bg-subtle/40 p-2">
              <div className="font-semibold text-primary">Rows read</div>
              <div className="mt-1 text-lg font-semibold tabular-nums">{preview.rowsRead}</div>
            </div>
            <div className="rounded-md border border-border-default bg-subtle/40 p-2">
              <div className="font-semibold text-primary">Valid rows</div>
              <div className="mt-1 text-lg font-semibold tabular-nums">{preview.rowsValid}</div>
            </div>
            <div className="rounded-md border border-border-default bg-subtle/40 p-2">
              <div className="font-semibold text-primary">Errors</div>
              <div className="mt-1 text-lg font-semibold tabular-nums">{preview.errors.length}</div>
            </div>
            <div className="rounded-md border border-border-default bg-subtle/40 p-2">
              <div className="font-semibold text-primary">Imported</div>
              <div className="mt-1 text-lg font-semibold tabular-nums">{preview.rowsImported}</div>
            </div>
          </div>

          {preview.errors.length > 0 ? (
            <Alert tone="danger">
              <div className="font-semibold">Fix these CSV rows before importing:</div>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {preview.errors.slice(0, 8).map((err) => (
                  <li key={`${err.rowNumber}-${err.message}`}>
                    Row {err.rowNumber}: {err.message}
                  </li>
                ))}
              </ul>
              {preview.errors.length > 8 ? <div className="mt-1">And {preview.errors.length - 8} more.</div> : null}
            </Alert>
          ) : null}

          {preview.parsedRows.length > 0 ? (
            <div className="max-h-72 overflow-auto rounded-md border border-border-default">
              <table className="min-w-full text-left text-xs">
                <thead className="sticky top-0 bg-subtle text-tertiary">
                  <tr>
                    <th className="px-2 py-1">Row</th>
                    <th className="px-2 py-1">Type</th>
                    <th className="px-2 py-1">Sr#</th>
                    <th className="px-2 py-1">Part #</th>
                    <th className="px-2 py-1">Noun</th>
                    <th className="px-2 py-1">UOM</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Unit price</th>
                    <th className="px-2 py-1">Make/brand</th>
                    <th className="px-2 py-1">Origin</th>
                    <th className="px-2 py-1">Remarks</th>
                    <th className="px-2 py-1">Parent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-default">
                  {preview.parsedRows.slice(0, 20).map((row) => (
                    <tr key={`${row.rowNumber}-${row.itemNumber}`}>
                      <td className="px-2 py-1 tabular-nums">{row.rowNumber}</td>
                      <td className="px-2 py-1">{row.itemType === 'SUB_PART' ? 'SUB-PART' : 'PART'}</td>
                      <td className="px-2 py-1 tabular-nums">{row.srNumber}</td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.itemNumber)}</td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.name)}</td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.uom)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{row.quantity}</td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {row.clientUnitPricePaisa != null ? formatCurrency(row.clientUnitPricePaisa, { prefix: false }) : '—'}
                      </td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.makeBrand)}</td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.origin)}</td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.remarks)}</td>
                      <td className="px-2 py-1">{formatUppercaseBusinessText(row.parent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.parsedRows.length > 20 ? (
                <div className="border-t border-border-default px-2 py-1 text-xs text-secondary">
                  Showing first 20 rows of {preview.parsedRows.length}.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Create part / sub-part modal
// ---------------------------------------------------------------------------

function CreateItemModal({
  project,
  parentId,
  existingSrNumbers,
  onClose,
}: {
  project: Project;
  parentId: string | null;
  /**
   * Sr#s of live sibling rows, used only to compute the suggested default.
   * The user can override (spec §6.1 — Sr# is manually entered).
   */
  existingSrNumbers: ReadonlyArray<number | null>;
  onClose: () => void;
}) {
  const isSubPart = parentId !== null;
  const create = useCreateProjectItem(project.id);
  const [name, setName] = useState('');
  const [itemNumber, setItemNumber] = useState('');
  const [uom, setUom] = useState('');
  const [qty, setQty] = useState('1');
  // Spec §6.1: Sr# is "manually entered, not auto-assigned". Pre-fill with
  // the suggested next value (max(existing) + 1, see suggestNextSrNumber)
  // but render it in an editable input so the user can override.
  const [srNumber, setSrNumber] = useState(() => String(suggestNextSrNumber(existingSrNumbers)));
  const [clientUnit, setClientUnit] = useState('');
  const [gstPct, setGstPct] = useState('');
  const [supplierUnit, setSupplierUnit] = useState('');
  const [makeBrand, setMakeBrand] = useState('');
  const [origin, setOrigin] = useState('');
  const [remarks, setRemarks] = useState('');
  const createDrafts = useDraftsList({
    draftType: 'SCHEDULE_OF_STORES',
    projectId: project.id,
    entityId: parentId,
  });
  const saveDraft = useSaveDraft();
  const discardDraft = useDiscardDraft();
  const latestCreateDraft =
    (createDrafts.data?.items ?? []).find((draft) => {
      if (draft.entityId !== parentId) return false;
      const payload = draft.payloadJson;
      return payload.mode === 'CREATE_ITEM' && payload.parentId === parentId;
    }) ?? null;

  const resumeCreateDraft = (): void => {
    if (!latestCreateDraft) return;
    const draft = latestCreateDraft.payloadJson;
    if (draft.mode !== 'CREATE_ITEM' || draft.parentId !== parentId) return;
    setSrNumber(draftString(draft.srNumber, srNumber));
    setItemNumber(draftString(draft.itemNumber));
    setName(draftString(draft.name));
    setUom(draftString(draft.uom));
    setQty(draftString(draft.qty, '1'));
    setClientUnit(draftString(draft.clientUnit));
    setGstPct(draftString(draft.gstPct));
    setSupplierUnit(draftString(draft.supplierUnit));
    setMakeBrand(draftString(draft.makeBrand));
    setOrigin(draftString(draft.origin));
    setRemarks(draftString(draft.remarks));
  };

  const saveCreateDraft = (): void => {
    saveDraft.mutate({
      draftType: 'SCHEDULE_OF_STORES',
      projectId: project.id,
      entityId: parentId,
      payloadJson: {
        mode: 'CREATE_ITEM',
        parentId,
        isSubPart,
        srNumber,
        itemNumber,
        name,
        uom,
        qty,
        clientUnit,
        gstPct,
        supplierUnit,
        makeBrand,
        origin,
        remarks,
        savedProjectRowVersion: project.rowVersion,
      },
    });
  };

  const discardCreateDraft = (): void => {
    if (!latestCreateDraft) return;
    discardDraft.mutate({ id: latestCreateDraft.id, rowVersion: latestCreateDraft.rowVersion });
  };

  const closeAfterCreate = (): void => {
    if (latestCreateDraft) {
      discardDraft.mutate({ id: latestCreateDraft.id, rowVersion: latestCreateDraft.rowVersion });
    }
    onClose();
  };

  const srNumberValid = srNumber !== '' && Number.isInteger(Number(srNumber)) && Number(srNumber) >= 1;
  // Unit price/GST are required on PART. Supplier price is optional on both
  // PART and SUB_PART; blank means "not recorded yet", not 0.
  const requiredPriceValid = isSubPart ? true : isValidNonNegativePriceInput(clientUnit);
  // Supplier price on PART is optional — if non-empty, it must still be valid.
  const optionalSupplierPriceValid = supplierUnit.trim() === '' || isValidNonNegativePriceInput(supplierUnit);
  const gstPctValid =
    isSubPart ||
    (gstPct.trim() !== '' && Number.isFinite(Number(gstPct)) && Number(gstPct) >= 0 && Number(gstPct) <= 100);
  const required =
    itemNumber.trim().length > 0 &&
    name.trim().length > 0 &&
    (isSubPart || uom.trim().length > 0) &&
    Number(qty) > 0 &&
    srNumberValid &&
    requiredPriceValid &&
    optionalSupplierPriceValid &&
    gstPctValid;
  const quantityPreview = Number.isFinite(Number(qty)) && Number(qty) > 0 ? Number(qty) : null;
  const unitPricePreviewPaisa =
    !isSubPart && isValidNonNegativePriceInput(clientUnit) ? Math.round(Number(clientUnit) * 100) : null;
  const gstRatePreviewBps =
    !isSubPart && gstPctValid && gstPct.trim() !== '' ? Math.round(Number(gstPct) * 100) : null;
  const valuePreviewPaisa =
    quantityPreview !== null && unitPricePreviewPaisa !== null
      ? quantityPreview * unitPricePreviewPaisa
      : null;
  const gstPreviewPaisa =
    valuePreviewPaisa !== null && gstRatePreviewBps !== null
      ? Math.round((valuePreviewPaisa * gstRatePreviewBps) / 10_000)
      : null;
  const totalPreviewPaisa =
    valuePreviewPaisa !== null && gstPreviewPaisa !== null
      ? valuePreviewPaisa + gstPreviewPaisa
      : null;
  const submit = (): void => {
    if (!required) return;
    const quantity = Number(qty);
    // The `required` guard above ensures the path-specific required price
    // is non-empty and valid, so we read it directly without a ?? 0 fallback.
    // The optional supplier price on a PART remains an undefined sentinel
    // when blank — keeping that semantics: blank = "leave for later", not 0.
    if (isSubPart) {
      const supplierPaisa = supplierUnit.trim() !== '' ? Math.round(Number(supplierUnit) * 100) : undefined;
      create.mutate(
        {
          itemType: 'SUB_PART',
          parentItemId: parentId!,
          srNumber: Number(srNumber),
          itemNumber: requiredBusiness(itemNumber),
          name: requiredBusiness(name),
          quantityPerParent: quantity,
          ...(supplierPaisa !== undefined ? { supplierUnitPricePaisa: supplierPaisa } : {}),
          makeBrand: optionalBusiness(makeBrand),
          origin: optionalBusiness(origin),
          remarks: optionalBusiness(remarks),
        },
        { onSuccess: closeAfterCreate },
      );
    } else {
      const clientPaisa = Math.round(Number(clientUnit) * 100);
      const supplierPaisa = supplierUnit.trim() !== ''
        ? Math.round(Number(supplierUnit) * 100)
        : undefined;
      const gstRateBps = Math.round(Number(gstPct) * 100);
      create.mutate(
        {
          itemType: 'PART',
          srNumber: Number(srNumber),
          itemNumber: requiredBusiness(itemNumber),
          name: requiredBusiness(name),
          uom: requiredBusiness(uom),
          partQuantity: quantity,
          clientUnitPricePaisa: clientPaisa,
          gstRateBps,
          ...(supplierPaisa !== undefined ? { supplierUnitPricePaisa: supplierPaisa } : {}),
          makeBrand: optionalBusiness(makeBrand),
          origin: optionalBusiness(origin),
          remarks: optionalBusiness(remarks),
        },
        { onSuccess: closeAfterCreate },
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isSubPart ? 'Add sub-part' : 'Add part'}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={saveCreateDraft}
            disabled={saveDraft.isPending}
            loading={saveDraft.isPending}
          >
            Save draft
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!required}
            loading={create.isPending}
          >
            {isSubPart ? 'Add sub-part' : 'Add part'}
          </Button>
        </>
      }
    >
      {create.error ? (
        <Alert tone="danger" className="mb-3">
          {create.error.message}
        </Alert>
      ) : null}
      {saveDraft.error ? (
        <Alert tone="danger" className="mb-3">
          {saveDraft.error.message}
        </Alert>
      ) : null}
      {discardDraft.error ? (
        <Alert tone="danger" className="mb-3">
          {discardDraft.error.message}
        </Alert>
      ) : null}
      {latestCreateDraft ? (
        <Alert tone="info" className="mb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>A saved Schedule of Stores draft is available for this {isSubPart ? 'sub-part' : 'part'}.</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="compact" type="button" onClick={resumeCreateDraft}>
                Resume draft
              </Button>
              <Button
                variant="tertiary"
                size="compact"
                type="button"
                loading={discardDraft.isPending}
                onClick={discardCreateDraft}
              >
                Discard
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}
      <FormField
        label="Sr#"
        required
        helpText={
          isSubPart
            ? "Position within this parent part's sub-part list."
            : 'Position in the parts list. Editable — defaults to next available.'
        }
      >
        <Input
          type="number"
          min={1}
          step={1}
          value={srNumber}
          onChange={(e) => setSrNumber(e.target.value)}
          className="font-mono tabular-nums"
        />
      </FormField>
      <FormField label={isSubPart ? 'Sub-part #' : 'Part #'} required>
        <Input
          value={itemNumber}
          onChange={(e) => setItemNumber(uppercaseInput(e.target.value))}
          placeholder={isSubPart ? 'e.g. SP-001' : 'e.g. PN-001'}
        />
      </FormField>
      <FormField label="Noun" required>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(uppercaseInput(e.target.value))}
          placeholder={isSubPart ? 'e.g. ROTARY JOINT' : 'e.g. RADAR MODULE'}
        />
      </FormField>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Make/brand">
          <Input
            value={makeBrand}
            onChange={(e) => setMakeBrand(uppercaseInput(e.target.value))}
            placeholder="e.g. HONEYWELL"
          />
        </FormField>
        <FormField label="Origin">
          <Input
            value={origin}
            onChange={(e) => setOrigin(uppercaseInput(e.target.value))}
            placeholder="e.g. USA"
          />
        </FormField>
      </div>
      <FormField label="Remarks">
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(uppercaseInput(e.target.value))}
          rows={2}
          className="w-full rounded-md border border-border-default bg-base px-3 py-2 text-sm text-primary outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          placeholder="e.g. MATCHED SET"
        />
      </FormField>
      {!isSubPart ? (
        <FormField label="UOM" required helpText="Unit of measure, e.g. NOS, SET, PAIR.">
          <Input value={uom} onChange={(e) => setUom(uppercaseInput(e.target.value))} placeholder="e.g. NOS" />
        </FormField>
      ) : null}
      <FormField label="Quantity" required>
        <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
      </FormField>
      {!isSubPart ? (
        <FormField
          label="Unit price (Rs.)"
          required
          helpText="Non-negative; 0 is allowed but means free-to-client."
        >
          <Input
            type="number"
            min={0}
            step="0.01"
            value={clientUnit}
            onChange={(e) => setClientUnit(e.target.value)}
            placeholder="e.g. 12500"
          />
        </FormField>
      ) : null}
      {!isSubPart ? (
        <FormField label="GST %" required helpText="Enter 18 for 18%. Use 0 when GST is not applicable.">
          <Input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={gstPct}
            onChange={(e) => setGstPct(e.target.value)}
            placeholder="e.g. 18"
          />
        </FormField>
      ) : null}
      {!isSubPart ? (
        <div className="mb-3 rounded-sm border border-border-default bg-subtle/40 px-3 py-2 text-xs text-secondary">
          <div className="font-semibold uppercase tracking-wide text-tertiary">Calculated values</div>
          <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-3">
            <span>Value: {valuePreviewPaisa !== null ? formatCurrency(valuePreviewPaisa) : '—'}</span>
            <span>GST amount: {gstPreviewPaisa !== null ? formatCurrency(gstPreviewPaisa) : '—'}</span>
            <span>Total value: {totalPreviewPaisa !== null ? formatCurrency(totalPreviewPaisa) : '—'}</span>
          </div>
        </div>
      ) : null}
      <FormField
        label="Supplier price (Rs.)"
        helpText={
          isSubPart
            ? 'Optional supplier price for this sub-part.'
            : 'Optional. Leave blank if the part has sub-parts (cost rolls up from them).'
        }
      >
        <Input
          type="number"
          min={0}
          step="0.01"
          value={supplierUnit}
          onChange={(e) => setSupplierUnit(e.target.value)}
        />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Edit part / sub-part modal
// ---------------------------------------------------------------------------

function paisaToInput(value: number | null | undefined): string {
  if (value == null) return '';
  return String(value / 100);
}

function bpsToPercentInput(value: number | null | undefined): string {
  if (value == null) return '';
  return String(value / 100);
}

function EditItemModal({
  project,
  item,
  onClose,
}: {
  project: Project;
  item: ProjectItem;
  onClose: () => void;
}) {
  const isSubPart = item.itemType === 'SUB_PART';
  const update = useUpdateProjectItem(project.id);

  const [srNumber, setSrNumber] = useState(() => String(item.srNumber ?? ''));
  const [itemNumber, setItemNumber] = useState(() => item.itemNumber ?? '');
  const [name, setName] = useState(() => item.name);
  const [uom, setUom] = useState(() => item.uom ?? '');
  const [qty, setQty] = useState(() =>
    String(isSubPart ? (item.quantityPerParent ?? 1) : (item.partQuantity ?? 1)),
  );
  const [clientUnit, setClientUnit] = useState(() => paisaToInput(item.clientUnitPricePaisa));
  const [gstPct, setGstPct] = useState(() => bpsToPercentInput(item.gstRateBps));
  const [supplierUnit, setSupplierUnit] = useState(() => paisaToInput(item.supplierUnitPricePaisa));
  const [makeBrand, setMakeBrand] = useState(() => item.makeBrand ?? '');
  const [origin, setOrigin] = useState(() => item.origin ?? '');
  const [remarks, setRemarks] = useState(() => item.remarks ?? '');
  const editDrafts = useDraftsList({
    draftType: 'SCHEDULE_OF_STORES',
    projectId: project.id,
    entityId: item.id,
  });
  const saveDraft = useSaveDraft();
  const discardDraft = useDiscardDraft();
  const latestEditDraft = editDrafts.data?.items[0] ?? null;

  const resumeEditDraft = (): void => {
    if (!latestEditDraft) return;
    const draft = latestEditDraft.payloadJson;
    if (draft.mode !== 'EDIT_ITEM' || draft.itemId !== item.id) return;
    setSrNumber(draftString(draft.srNumber, srNumber));
    setItemNumber(draftString(draft.itemNumber, item.itemNumber ?? ''));
    setName(draftString(draft.name, item.name));
    setUom(draftString(draft.uom, item.uom ?? ''));
    setQty(draftString(draft.qty, String(isSubPart ? (item.quantityPerParent ?? 1) : (item.partQuantity ?? 1))));
    setClientUnit(draftString(draft.clientUnit, paisaToInput(item.clientUnitPricePaisa)));
    setGstPct(draftString(draft.gstPct, bpsToPercentInput(item.gstRateBps)));
    setSupplierUnit(draftString(draft.supplierUnit, paisaToInput(item.supplierUnitPricePaisa)));
    setMakeBrand(draftString(draft.makeBrand, item.makeBrand ?? ''));
    setOrigin(draftString(draft.origin, item.origin ?? ''));
    setRemarks(draftString(draft.remarks, item.remarks ?? ''));
  };

  const saveEditDraft = (): void => {
    saveDraft.mutate({
      draftType: 'SCHEDULE_OF_STORES',
      projectId: project.id,
      entityId: item.id,
      payloadJson: {
        mode: 'EDIT_ITEM',
        itemId: item.id,
        itemRowVersion: item.rowVersion,
        srNumber,
        itemNumber,
        name,
        uom,
        qty,
        clientUnit,
        gstPct,
        supplierUnit,
        makeBrand,
        origin,
        remarks,
        savedProjectRowVersion: project.rowVersion,
      },
    });
  };

  const discardEditDraft = (): void => {
    if (!latestEditDraft) return;
    discardDraft.mutate({ id: latestEditDraft.id, rowVersion: latestEditDraft.rowVersion });
  };

  const closeAfterEdit = (): void => {
    if (latestEditDraft) {
      discardDraft.mutate({ id: latestEditDraft.id, rowVersion: latestEditDraft.rowVersion });
    }
    onClose();
  };

  const srNumberValid = srNumber !== '' && Number.isInteger(Number(srNumber)) && Number(srNumber) >= 1;
  const requiredPriceValid = isSubPart ? true : isValidNonNegativePriceInput(clientUnit);
  const optionalSupplierPriceValid = supplierUnit.trim() === '' || isValidNonNegativePriceInput(supplierUnit);
  const gstPctValid =
    isSubPart ||
    (gstPct.trim() !== '' &&
      Number.isFinite(Number(gstPct)) &&
      Number(gstPct) >= 0 &&
      Number(gstPct) <= 100);
  const required =
    itemNumber.trim().length > 0 &&
    name.trim().length > 0 &&
    (isSubPart || uom.trim().length > 0) &&
    Number(qty) > 0 &&
    srNumberValid &&
    requiredPriceValid &&
    optionalSupplierPriceValid &&
    gstPctValid;

  const quantityPreview = Number.isFinite(Number(qty)) && Number(qty) > 0 ? Number(qty) : null;
  const unitPricePreviewPaisa =
    !isSubPart && isValidNonNegativePriceInput(clientUnit)
      ? Math.round(Number(clientUnit) * 100)
      : null;
  const gstRatePreviewBps =
    !isSubPart && gstPctValid && gstPct.trim() !== '' ? Math.round(Number(gstPct) * 100) : null;
  const valuePreviewPaisa =
    quantityPreview !== null && unitPricePreviewPaisa !== null
      ? quantityPreview * unitPricePreviewPaisa
      : null;
  const gstPreviewPaisa =
    valuePreviewPaisa !== null && gstRatePreviewBps !== null
      ? Math.round((valuePreviewPaisa * gstRatePreviewBps) / 10_000)
      : null;
  const totalPreviewPaisa =
    valuePreviewPaisa !== null && gstPreviewPaisa !== null
      ? valuePreviewPaisa + gstPreviewPaisa
      : null;

  const submit = (): void => {
    if (!required) return;
    const quantity = Number(qty);
    const supplierPaisa =
      supplierUnit.trim() !== '' ? Math.round(Number(supplierUnit) * 100) : null;

    if (isSubPart) {
      update.mutate(
        {
          itemId: item.id,
          rowVersion: item.rowVersion,
          srNumber: Number(srNumber),
          itemNumber: optionalBusiness(itemNumber),
          name: requiredBusiness(name),
          quantityPerParent: quantity,
          supplierUnitPricePaisa: supplierPaisa,
          makeBrand: optionalBusiness(makeBrand),
          origin: optionalBusiness(origin),
          remarks: optionalBusiness(remarks),
        },
        { onSuccess: closeAfterEdit },
      );
      return;
    }

    update.mutate(
      {
        itemId: item.id,
        rowVersion: item.rowVersion,
        srNumber: Number(srNumber),
        itemNumber: optionalBusiness(itemNumber),
        name: requiredBusiness(name),
        uom: requiredBusiness(uom),
        partQuantity: quantity,
        clientUnitPricePaisa: Math.round(Number(clientUnit) * 100),
        gstRateBps: Math.round(Number(gstPct) * 100),
        supplierUnitPricePaisa: supplierPaisa,
        makeBrand: optionalBusiness(makeBrand),
        origin: optionalBusiness(origin),
        remarks: optionalBusiness(remarks),
      },
      { onSuccess: closeAfterEdit },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isSubPart ? 'Edit sub-part' : 'Edit part'}
      description="Schedule of Stores entries are saved in uppercase where applicable."
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={saveEditDraft}
            disabled={saveDraft.isPending}
            loading={saveDraft.isPending}
          >
            Save draft
          </Button>
          <Button variant="primary" onClick={submit} disabled={!required} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      {update.error ? (
        <Alert tone="danger" className="mb-3">
          {update.error.message}
        </Alert>
      ) : null}
      {saveDraft.error ? (
        <Alert tone="danger" className="mb-3">
          {saveDraft.error.message}
        </Alert>
      ) : null}
      {discardDraft.error ? (
        <Alert tone="danger" className="mb-3">
          {discardDraft.error.message}
        </Alert>
      ) : null}
      {latestEditDraft ? (
        <Alert tone="info" className="mb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>A saved Schedule of Stores draft is available for this row.</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="compact" type="button" onClick={resumeEditDraft}>
                Resume draft
              </Button>
              <Button
                variant="tertiary"
                size="compact"
                type="button"
                loading={discardDraft.isPending}
                onClick={discardEditDraft}
              >
                Discard
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}

      <FormField label="Sr#" required>
        <Input
          type="number"
          min={1}
          step={1}
          value={srNumber}
          onChange={(e) => setSrNumber(e.target.value)}
          className="font-mono tabular-nums"
        />
      </FormField>
      <FormField label={isSubPart ? 'Sub-part #' : 'Part #'} required>
        <Input
          value={itemNumber}
          onChange={(e) => setItemNumber(uppercaseInput(e.target.value))}
          placeholder={isSubPart ? 'e.g. SP-001' : 'e.g. PN-001'}
        />
      </FormField>
      <FormField label="Noun" required>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(uppercaseInput(e.target.value))}
          placeholder={isSubPart ? 'e.g. ROTARY JOINT' : 'e.g. RADAR MODULE'}
        />
      </FormField>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Make/brand">
          <Input
            value={makeBrand}
            onChange={(e) => setMakeBrand(uppercaseInput(e.target.value))}
            placeholder="e.g. HONEYWELL"
          />
        </FormField>
        <FormField label="Origin">
          <Input
            value={origin}
            onChange={(e) => setOrigin(uppercaseInput(e.target.value))}
            placeholder="e.g. USA"
          />
        </FormField>
      </div>
      <FormField label="Remarks">
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(uppercaseInput(e.target.value))}
          rows={2}
          className="w-full rounded-md border border-border-default bg-base px-3 py-2 text-sm text-primary outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          placeholder="e.g. MATCHED SET"
        />
      </FormField>
      {!isSubPart ? (
        <FormField label="UOM" required helpText="Unit of measure, e.g. NOS, SET, PAIR.">
          <Input value={uom} onChange={(e) => setUom(uppercaseInput(e.target.value))} />
        </FormField>
      ) : null}
      <FormField label="Quantity" required>
        <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
      </FormField>
      {!isSubPart ? (
        <FormField label="Unit price (Rs.)" required>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={clientUnit}
            onChange={(e) => setClientUnit(e.target.value)}
          />
        </FormField>
      ) : null}
      {!isSubPart ? (
        <FormField label="GST %" required>
          <Input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={gstPct}
            onChange={(e) => setGstPct(e.target.value)}
          />
        </FormField>
      ) : null}
      {!isSubPart ? (
        <div className="mb-3 rounded-sm border border-border-default bg-subtle/40 px-3 py-2 text-xs text-secondary">
          <div className="font-semibold uppercase tracking-wide text-tertiary">Calculated values</div>
          <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-3">
            <span>Value: {valuePreviewPaisa !== null ? formatCurrency(valuePreviewPaisa) : '—'}</span>
            <span>GST amount: {gstPreviewPaisa !== null ? formatCurrency(gstPreviewPaisa) : '—'}</span>
            <span>Total value: {totalPreviewPaisa !== null ? formatCurrency(totalPreviewPaisa) : '—'}</span>
          </div>
        </div>
      ) : null}
      <FormField
        label="Supplier price (Rs.)"
        helpText={
          isSubPart
            ? 'Optional supplier price for this sub-part. Leave blank to clear.'
            : 'Optional. Leave blank to clear when cost rolls up from sub-parts.'
        }
      >
        <Input
          type="number"
          min={0}
          step="0.01"
          value={supplierUnit}
          onChange={(e) => setSupplierUnit(e.target.value)}
        />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Reject parts submission modal
// ---------------------------------------------------------------------------

function RejectModal({
  projectId,
  submissionId,
  onClose,
}: {
  projectId: string;
  submissionId: string;
  onClose: () => void;
}) {
  const reject = useRejectSubmission(projectId);
  const [comment, setComment] = useState('');

  const submit = (): void => {
    reject.mutate(
      { submissionId, rejectionComment: comment.trim() || undefined },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Reject parts submission"
      description="The parts workflow returns to ENTERING. The submitter can revise and resubmit."
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} loading={reject.isPending}>
            Reject
          </Button>
        </>
      }
    >
      {reject.error ? (
        <Alert tone="danger" className="mb-3">
          {reject.error.message}
        </Alert>
      ) : null}
      <FormField label="Reason (optional)" helpText="Visible to the submitter on the parts tab.">
        <Input
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="What needs to change?"
        />
      </FormField>
    </Modal>
  );
}
